import type { ApolloClient } from "@apollo/client";
import type { GetUserQuery } from "graphql/generated";
import { getProjectIdFromPath } from "utils/getProjectIdFromPath";
import { history } from "utils/history";
import { parseSelectedProjectId } from "utils/parseSelectedProjectId";
import type { IdpErrorReasons } from "./constants";
import { GET_USER } from "./query";

// Redirect to the full-page IDP/outage error screen. A hard redirect (rather
// than an in-app navigation) keeps this decoupled from the router: everything
// the page needs travels in the URL, so a reload preserves it, and it avoids an
// import cycle with router.tsx (which reads `client` from api/client). The
// pathname guard prevents a reload loop if landing here re-triggers the error.
export const navigateToIdpError = (search: {
  reason: IdpErrorReasons;
  operation?: string;
}) => {
  if (window.location.pathname === "/idpError") return;

  const params = new URLSearchParams({ reason: search.reason });
  if (search.operation) params.set("operation", search.operation);

  window.location.replace(`/idpError?${params}`);
};

/**
 * The current project id for code that cannot use `useProjectId`: the URL's
 * project or, outside project routes, the user's saved selection in the cached
 * `GET_USER`. Takes the client so this file does not import `api/client`.
 */
export const getCurrentProjectId = (client: ApolloClient<object>) =>
  getProjectIdFromPath(history.location.pathname) ??
  parseSelectedProjectId(
    client.readQuery<GetUserQuery>({ query: GET_USER })?.getUser.uiState,
  );
